import { forwardRef, useCallback, useState, type TextareaHTMLAttributes } from 'react';
import { cn } from '@restworld/utils-common';

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  /** Called with the textarea string value on change */
  onChange?: (value: string) => void;
  /** Show character count when maxLength is set */
  showMaxLength?: boolean;
  /** Error state - applies red border/background */
  isError?: boolean;
  /** Test ID for data-cy attribute */
  testId?: string;
  /** Label displayed above the textarea */
  label?: string;
  /** Support text displayed below the textarea */
  supportText?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      onChange,
      id,
      rows = 4,
      maxLength,
      showMaxLength = true,
      isError,
      testId,
      label,
      supportText,
      className,
      value,
      ...rest
    },
    ref
  ) => {
    const [charCount, setCharCount] = useState(() => (typeof value === 'string' ? value.length : 0));

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (maxLength) {
          setCharCount(e.target.value.length);
        }
        onChange?.(e.target.value);
      },
      [maxLength, onChange]
    );

    const remaining = maxLength ? maxLength - charCount : undefined;

    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        {label ? (
          <label htmlFor={id} className="text-base font-semibold leading-6">
            {label}
          </label>
        ) : null}
        <div className="relative">
          <textarea
            ref={ref}
            id={id}
            rows={rows}
            maxLength={maxLength}
            value={value}
            onChange={handleChange}
            data-cy={testId}
            className={cn(
              'w-full rounded-lg border border-neutral-90 bg-white px-3 py-3 text-base',
              'placeholder:text-neutral-200',
              'focus:border-primary-300 focus:ring-primary-300 focus:outline-none focus:ring-1',
              'disabled:cursor-not-allowed disabled:bg-neutral-40 disabled:opacity-70',
              isError && 'border-red-500 bg-red-50'
            )}
            {...rest}
          />
          {maxLength && showMaxLength ? (
            <span className="absolute right-3 bottom-2 text-xs font-normal text-gray-400">
              {remaining}/{maxLength}
            </span>
          ) : null}
        </div>
        {supportText ? (
          <p className={cn('text-sm font-normal', isError ? 'text-red-600' : 'text-neutral-700')}>{supportText}</p>
        ) : null}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';
